import { LegalClientBodyType } from 'api/types/entity';
import { GridFields, InfoCard } from 'components';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Chip, MenuOption } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import { Colors } from 'libs/ui/theme';

import { BodyItem, BodyOptionType } from './types';

export interface BodiesInfoCardProps {
  items: BodyItem[];
  editable?: boolean;
  onClickDelete?: (row: BodyItem) => void;
  onCellClick: (row: BodyItem) => void;
  onClickArchive?: (row: BodyItem) => void;
  onClickEdit?: (row: BodyItem) => void;
}

export const BodiesInfoCard = ({
  items,
  editable,
  onCellClick,
  onClickArchive,
  onClickDelete,
  onClickEdit,
}: BodiesInfoCardProps) => {
  const { t } = useTranslation();

  const shownOptions: Partial<Record<BodyOptionType, boolean>> = {
    delete: editable && !!onClickDelete,
    archive: editable && !!onClickArchive,
    dearchive: editable && !!onClickArchive,
    edit: editable,
  };

  const onClickMenuOption = (
    item: BodyItem,
    option: MenuOption<BodyOptionType>
  ) => {
    switch (option.value) {
      case BodyOptionType.delete:
        onClickDelete?.(item);
        break;
      case BodyOptionType.archive:
      case BodyOptionType.dearchive:
        onClickArchive?.(item);
        break;
      case BodyOptionType.edit:
        onClickEdit?.(item);
        break;
      default:
        break;
    }
  };

  return (
    <GridFields>
      {items.map((item) => (
        <InfoCard
          fields={[
            {
              label: t('client.legal.companyStructure.columns.type'),
              value:
                item.type === LegalClientBodyType.Other
                  ? item.otherType ?? ''
                  : t(`client.legal.companyStructure.bodyTypes.${item.type}`),
            },
            {
              label: t('client.legal.companyStructure.columns.status'),
              value: (
                <Chip
                  size="xs"
                  text={
                    item.archived
                      ? t('common.archivedStatus')
                      : t('common.activeStatus')
                  }
                  variant={item.archived ? 'gray' : 'green'}
                />
              ),
            },
          ]}
          menuOptions={
            editable
              ? lodash.compact([
                  shownOptions.edit &&
                    !item.archived && {
                      value: BodyOptionType.edit,
                      label: t('common.edit'),
                      Icon: Icon.Edit,
                    },
                  shownOptions.archive &&
                    !item.archived && {
                      value: BodyOptionType.archive,
                      label: t('common.archive'),
                      Icon: Icon.TimeSleep,
                    },
                  shownOptions.dearchive &&
                    item.archived && {
                      value: BodyOptionType.dearchive,
                      label: t('common.dearchive'),
                      Icon: Icon.RotateRightCheck,
                    },
                  shownOptions.delete && {
                    value: BodyOptionType.delete,
                    label: t('common.delete'),
                    Icon: Icon.Bucket,
                    color: Colors.red,
                  },
                ])
              : undefined
          }
          title={item.name}
          variant={item.archived ? 'gray' : 'default'}
          onClick={() => onCellClick(item)}
          onClickMenuOption={(option) => onClickMenuOption(item, option)}
        />
      ))}
    </GridFields>
  );
};
